import { useNavigate, useParams, useLocation, Outlet } from 'react-router';
import {
  ArrowUpTrayIcon,
  PlusIcon,
  TableCellsIcon,
  PlusCircleIcon,
  CodeBracketIcon,
} from '@heroicons/react/24/outline';
import AspenModelProcessingTable from './aspen/AspenModelProcessingTable';
import { useIsFeatureEnabled } from '@/stores/settingsStore';

const ChoiceModal = () => {
  const { pathwayId, projectId, caseId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const showEquationBuilder = useIsFeatureEnabled('equation_builder');

  const isChildRoute = location.pathname.includes('/choose-model/');
  const handleClose = () => {
    navigate(`/projects/${projectId}/build/pathways/${pathwayId}/${caseId}`, { replace: true });
  };

  const handleImportExcel = () => {
    navigate(`import-model`);
  };

  const handleCreateCustomModule = () => {
    navigate(`create-model`);
  };

  const handleCreateEquationModule = () => {
    navigate(`create-equation-model`);
  };

  if (isChildRoute) {
    return <Outlet />;
  }

  return (
    <dialog className="modal" open>
      <div className="modal-box w-full max-w-6xl p-10">
        <button className="btn btn-sm btn-circle btn-ghost absolute right-2 top-2" onClick={handleClose}>
          ✕
        </button>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="border border-gray-200 rounded-lg p-6 flex flex-col items-center hover:shadow-md transition-shadow">
            <div className="bg-blue-100 p-3 rounded-full mb-4">
              <PlusCircleIcon className="w-6 h-6 text-info" />
            </div>
            <h4 className="font-medium text-center mb-2">Create a Ratio Module</h4>
            <p className="text-sm text-gray-600 text-center mb-4">
              The quickest option: fixed input and output amounts that scale with your pathway.
            </p>
            <button onClick={handleCreateCustomModule} className="btn btn-sm btn-accent gap-2 mt-auto w-full">
              <PlusIcon className="w-4 h-4 shrink-0" />
              Create a Ratio Module
            </button>
          </div>

          <div
            className={`border border-gray-200 rounded-lg p-6 flex flex-col items-center transition-shadow ${showEquationBuilder ? 'hover:shadow-md' : 'opacity-60'}`}
          >
            <div className="bg-green-100 p-3 rounded-full mb-4">
              <CodeBracketIcon className="w-6 h-6 text-green-600" />
            </div>
            <h4 className="font-medium text-center mb-2">Create with Equations</h4>
            <p className="text-sm text-gray-600 text-center mb-4">
              Define inputs, outputs, parameters, and balance equations in code.
            </p>
            {showEquationBuilder ? (
              <button onClick={handleCreateEquationModule} className="btn btn-sm btn-accent gap-2 mt-auto w-full">
                <PlusIcon className="w-4 h-4 shrink-0" />
                Create with Equations
              </button>
            ) : (
              <span className="mt-auto text-sm text-gray-400 italic">Coming soon</span>
            )}
          </div>

          <div className="border border-gray-200 rounded-lg p-6 flex flex-col items-center hover:shadow-md transition-shadow">
            <div className="bg-purple-100 p-3 rounded-full mb-4">
              <TableCellsIcon className="w-6 h-6 text-purple-600" />
            </div>
            <h4 className="font-medium text-center mb-2">Import from Excel</h4>
            <p className="text-sm text-gray-600 text-center mb-4">
              Import your own excel spreadsheet to use it in Sesame.
            </p>
            <button onClick={handleImportExcel} className="btn btn-sm btn-accent gap-2 mt-auto w-full">
              <ArrowUpTrayIcon className="w-4 h-4 shrink-0" />
              Import from Excel
            </button>
          </div>

          <div className="border border-gray-200 rounded-lg p-6 flex flex-col items-center hover:shadow-md transition-shadow">
            <div className="bg-orange-100 rounded-full mb-4 flex items-center justify-center w-10 h-10">
              <span className="text-orange-600">A+</span>
            </div>
            <h4 className="font-medium text-center mb-2">
              Import from Aspen Plus<sup>®</sup> or HYSYS<sup>®</sup>
            </h4>
            <p className="text-sm text-gray-600 text-center mb-4">Upload an Aspen Simulation file to use in Sesame.</p>
            <button
              onClick={() => navigate(`import-from-aspen`)}
              className="btn btn-sm btn-accent gap-1 mt-auto w-full"
            >
              <ArrowUpTrayIcon className="w-4 h-4 shrink-0" />
              Import from Aspen
            </button>
          </div>
        </div>

        <AspenModelProcessingTable />
      </div>
    </dialog>
  );
};

export default ChoiceModal;
